import { api, isAuthenticated, setAuthenticated, clearAuthenticated } from '../apiClient';

export const authService = {
  /** GET /auth/me — skips network call if the session flag isn't set (user definitely not logged in) */
  me: () => {
    if (!isAuthenticated()) return Promise.resolve(null);
    return api.get('/auth/me');
  },

  /** POST /auth/login — backend sets access_token + refresh_token cookies.
   *  If 2FA is enabled, returns { twoFactorRequired: true, tempToken } without cookies. */
  login: async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    localStorage.removeItem('jwt_token'); // purge any pre-migration token
    // Only mark as authenticated when 2FA is NOT required (cookies are set by backend)
    if (!data.twoFactorRequired) setAuthenticated();
    return data;
  },

  /** POST /auth/2fa/verify — exchange tempToken + TOTP code for full JWT cookies */
  verify2fa: async (tempToken, code) => {
    const data = await api.post('/auth/2fa/verify', { tempToken, code });
    setAuthenticated();
    return data;
  },

  /** POST /auth/2fa/setup — generate TOTP secret, returns { secret, otpauthUri } */
  setup2fa: () => api.post('/auth/2fa/setup'),

  /** POST /auth/2fa/enable — confirm setup with a TOTP code */
  enable2fa: (code) => api.post('/auth/2fa/enable', { code }),

  /** DELETE /auth/2fa/disable — disable 2FA after confirming with a TOTP code */
  disable2fa: (code) => api.delete('/auth/2fa/disable', { data: { code } }),

  /** POST /auth/register — backend sets access_token + refresh_token cookies */
  register: async ({ firstName, lastName, email, password, phone } = {}) => {
    const data = await api.post('/auth/register', { firstName, lastName, email, password, phone });
    localStorage.removeItem('jwt_token'); // purge any pre-migration token
    setAuthenticated();
    return data;
  },

  /** POST /auth/logout — backend clears cookies; we clear the local flag. Navigation is handled by the caller. */
  logout: async () => {
    try { await api.post('/auth/logout'); } catch { /* expired is fine */ }
    clearAuthenticated();
  },

  /** Navigate to login without logging out */
  redirectToLogin: (returnUrl) => {
    const url = returnUrl
      ? `/Login?returnUrl=${encodeURIComponent(returnUrl)}`
      : `/Login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    window.location.href = url;
  },

  /** POST /auth/refresh — backend reads refresh_token cookie, sets new access_token cookie */
  refresh: () => api.post('/auth/refresh'),

  /** POST /auth/forgot-password */
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),

  /** POST /auth/reset-password */
  resetPassword: (token, newPassword) =>
    api.post('/auth/reset-password', { token, newPassword }),

  /** PUT /auth/me/password */
  changePassword: (currentPassword, newPassword) =>
    api.put('/auth/me/password', { currentPassword, newPassword }),

  /** DELETE /auth/me — permanently deletes the current account.
   *  Password required for password-based accounts; omit for OAuth-only accounts. */
  deleteAccount: async (password) => {
    await api.delete('/auth/me', { data: password ? { password } : {} });
    clearAuthenticated();
  },

  /** POST /auth/activate */
  activate: (code) => api.post('/auth/activate', null, { params: { code } }),

  /** POST /auth/resend-activation */
  resendActivation: (email) =>
    api.post('/auth/resend-activation', null, { params: { email } }),
};
